import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Button, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { AutoModel } from "../../components/common";
import { AlertMessage } from "../../components/common/AlertMessage";
// 预估积分先不展示
// import { CoinsEstimateIcon } from "../../components/common/CoinsEstimateIcon";
import { useAlert } from "../../hooks/useAlert";
import { triggerPointsRefresh } from "../../service/points";
import {
  DIGITAL_HUMAN_SCOPE,
  digitalHumanUsable,
} from "../../service/digitalHuman";
import {
  VIDEO_INFER_DIRECTION,
  VIDEO_INFER_SCENE_TYPE,
  createVideoInferApi,
  createVideoInferWithAudioApi,
  deleteVideoInferApi,
  estimateVideoInferPointsApi,
  genVideoInferScriptApi,
  getVideoInferApi,
  pickVideoInferScene,
  renameVideoInferApi,
  resolveVideoInferCoverUrl,
  resolveVideoInferMediaUrl,
  resolveVideoInferVideoUrl,
  retryVideoInferApi,
  saveVideoInferAudioDraftApi,
  saveVideoInferDraftApi,
  videoInferUiStatus,
  videoInferWorkType,
} from "../../service/videoInfer";
import {
  BG_COLORS,
  LANGUAGES,
  RATIOS,
  SAMPLE_AVATARS,
  type WorkStatus,
  isTransparentBg,
  statusText,
} from "./data";
import { useDigitalHumanVoiceLists } from "./useDigitalHumanVoiceLists";
import { AvatarPickerModal, VoicePickerModal, BackgroundPickerModal } from "./pickers";
import { useDigitalHumanPage } from "./useDigitalHumanPage";
import { useVideoInferPage } from "./useVideoInferPage";

const InputIcon = ({ name }: { name: "text" | "audio" | "ppt" }) => {
  if (name === "text") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 6V3h16v3M12 3v18m-4 0h8" />
      </svg>
    );
  }
  if (name === "audio") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 14v-3a9 9 0 0 1 18 0v3" />
        <rect x="3" y="12" width="4" height="9" rx="2" />
        <rect x="17" y="12" width="4" height="9" rx="2" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8m-8 4h8" />
    </svg>
  );
};

const UiIcon = ({ d }: { d: string }) => (
  <svg className="adh-ui-icon" viewBox="0 0 24 24" aria-hidden="true">
    <path d={d} />
  </svg>
);

const ChevronIcon = () => (
  <svg className="adh-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg className="adh-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

type NativeSelectOption = string | { value: string; label: string };

const NativeSelect: FC<{
  value: string;
  options: NativeSelectOption[];
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
}> = ({ value, options, onChange, className, disabled }) => {
  const rootRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const items = options.map((opt) => (typeof opt === "string" ? { value: opt, label: opt } : opt));
  const currentLabel = items.find((item) => item.value === value)?.label ?? value;

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span
      ref={rootRef}
      className={`adh-compact-select adh-native-select${open ? " is-open" : ""}${disabled ? " is-disabled" : ""}${className ? ` ${className}` : ""}`}
    >
      <button
        type="button"
        className="adh-native-select__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
      >
        <strong>{currentLabel}</strong>
        <ChevronDownIcon />
      </button>
      {open && (
        <ul className="adh-select-menu" role="listbox">
          {items.map((opt) => (
            <li key={opt.value}>
              <button
                type="button"
                role="option"
                aria-selected={opt.value === value}
                className={opt.value === value ? "is-active" : undefined}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
};

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 18H6a4 4 0 0 1-1-8 7 7 0 0 1 13-3 5 5 0 0 1 1 10" />
    <path d="M12 21V10" />
    <path d="m8 14 4-4 4 4" />
  </svg>
);

const AudioFileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18V5l12-2v13M9 9l12-2" />
    <ellipse cx="6" cy="18" rx="3" ry="3" />
    <ellipse cx="18" cy="16" rx="3" ry="3" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 5 11 7-11 7z" />
  </svg>
);

const RetryIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 7v5h-5M4 17v-5h5M6.1 6.1A8 8 0 0 1 20 12M4 12a8 8 0 0 0 13.9 5.9" />
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12m-4-4 4 4 4-4M4 16v5h16v-5" />
  </svg>
);

const PencilIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 4 5 5M4 20l5-1L21 7a2 2 0 0 0-5-5L4 14z" />
  </svg>
);

const MoreIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="5" cy="12" r="1.6" />
    <circle cx="12" cy="12" r="1.6" />
    <circle cx="19" cy="12" r="1.6" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7m4-7v7" />
  </svg>
);

function formatWorkDate(value?: string): string {
  const raw = String(value || "").trim().replace(/^["'`]+|["'`]+$/g, "");
  if (!raw) return "";
  const date = new Date(raw.includes("T") ? raw : raw.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return raw;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function workRatio(item: VideoInfer.Item): string {
  const raw = String(item.direction ?? "").trim();
  if (raw === VIDEO_INFER_DIRECTION.PORTRAIT) return "9:16";
  if (raw === VIDEO_INFER_DIRECTION.LANDSCAPE) return "16:9";
  return /^\d+:\d+$/.test(raw) ? raw : "16:9";
}

const captionsLanguageSupported = (lang: string) => lang === "zh" || lang === "en";

const captionsPayload = (lang: string, enabled: boolean): { captions?: number } =>
  captionsLanguageSupported(lang) ? { captions: enabled ? 1 : 0 } : {};

const AUDIO_ACCEPT = "audio/mpeg,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,.mp3,.wav,.m4a";
const AUDIO_MAX_BYTES = 100 * 1024 * 1024;

const fileExt = (name: string) => {
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1).toUpperCase() : "FILE";
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDuration = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return "00:00";
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const isAllowedAudioFile = (file: File) =>
  ["mp3", "wav", "m4a"].includes(fileExt(file.name).toLowerCase());

const workNameFromFile = (file: File) => {
  const name = file.name.replace(/\.[^.]+$/, "").trim() || "音频口播";
  return name.slice(0, 255);
};

const ratioToDirection = (ratio: string) =>
  ratio === "9:16" ? VIDEO_INFER_DIRECTION.PORTRAIT : VIDEO_INFER_DIRECTION.LANDSCAPE;

const readAudioDurationSeconds = (file: File): Promise<number> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = document.createElement("audio");
    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      const duration = audio.duration;
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(duration) && duration > 0 ? duration : 0);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(0);
    };
    audio.src = url;
  });

const readAudioDurationFromUrl = (url: string): Promise<number> =>
  new Promise((resolve) => {
    const audio = document.createElement("audio");
    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      const duration = audio.duration;
      resolve(Number.isFinite(duration) && duration > 0 ? duration : 0);
    };
    audio.onerror = () => resolve(0);
    audio.src = url;
  });

type StudioWork = {
  id: string;
  type: "talking" | "course";
  title: string;
  status: WorkStatus;
  date: string;
  ratio: string;
  videoUrl?: string | null;
  coverUrl?: string | null;
  captions?: number;
  targetLanguage?: string;
  raw?: VideoInfer.Item;
};

const WorkVideoThumb: FC<{ src: string }> = ({ src }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    setReady(false);
    video.setAttribute("src", `${src}#t=0.1`);
    video.load();
  }, [src]);

  return (
    <video
      ref={videoRef}
      className={`adh-work-video${ready ? " is-loaded" : ""}`}
      muted
      playsInline
      preload="metadata"
      onLoadedMetadata={(e) => {
        try {
          (e.currentTarget as HTMLVideoElement).currentTime = 0.1;
        } catch {
          /* ignore */
        }
      }}
      onLoadedData={() => setReady(true)}
    />
  );
};

const WorkCard: FC<{
  work: StudioWork;
  onPreview: () => void;
  onCopy: () => void;
  onDownload: () => void;
  onRename: () => void;
  onDelete: () => void;
  onResume: () => void;
  onRetry: () => void;
  retrying?: boolean;
}> = ({ work, onPreview, onCopy, onDownload, onRename, onDelete, onResume, onRetry, retrying }) => {
  const menuRef = useRef<HTMLDetailsElement>(null);
  const playable = Boolean(work.videoUrl) && work.status === "done";
  const failed = work.status === "failed";
  const draft = work.status === "draft";
  const showActions = !draft && !failed;

  useEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const closeIfOutside = (e: MouseEvent) => {
      if (!menu.open) return;
      if (!menu.contains(e.target as Node)) menu.open = false;
    };
    const closeOnEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && menu.open) menu.open = false;
    };
    document.addEventListener("mousedown", closeIfOutside);
    document.addEventListener("keydown", closeOnEsc);
    return () => {
      document.removeEventListener("mousedown", closeIfOutside);
      document.removeEventListener("keydown", closeOnEsc);
    };
  }, []);

  const closeOtherMenus = () => {
    if (!menuRef.current?.open) return;
    document.querySelectorAll<HTMLDetailsElement>(".adh-work-actions[open]").forEach((el) => {
      if (el !== menuRef.current) el.open = false;
    });
  };

  return (
    <article className={`adh-work-card${playable ? " is-playable" : ""}`}>
      <div className={`adh-work-cover${work.type === "course" ? " is-course" : ""}`}>
        {work.type === "course" ? <span className="adh-cover-slide" /> : null}
        <i className="adh-cover-human" />
        {work.videoUrl ? <WorkVideoThumb src={work.videoUrl} /> : null}
        <span className={`adh-work-status ${work.status}`}>{statusText(work.status)}</span>
        {draft ? (
          <button
            type="button"
            className="adh-draft-open"
            aria-label={`继续编辑${work.title}`}
            title="继续编辑草稿"
            onClick={onResume}
          />
        ) : null}
        {playable ? (
          <button
            type="button"
            className="adh-work-play"
            aria-label={`查看${work.title}`}
            onClick={onPreview}
          >
            <PlayIcon />
          </button>
        ) : null}
        {failed ? (
          <button
            type="button"
            className="adh-work-play adh-work-retry"
            aria-label={`重试${work.title}`}
            title="重试"
            disabled={retrying}
            onClick={onRetry}
          >
            <RetryIcon />
          </button>
        ) : null}
        {draft ? (
          <div className="adh-work-quick-actions is-draft">
            <button type="button" className="adh-work-view-button" title="继续编辑草稿" onClick={onResume}>
              继续编辑
            </button>
          </div>
        ) : null}
        {showActions ? (
          <div className="adh-work-quick-actions">
            <button
              type="button"
              className="adh-work-view-button"
              disabled={!playable}
              title={playable ? "查看作品" : "作品完成后可查看"}
              onClick={onPreview}
            >
              <EyeIcon />
              查看作品
            </button>
            <button type="button" title="创建副本后编辑，不改动原作品" onClick={onCopy}>
              再次编辑
            </button>
            <button
              type="button"
              className="adh-work-download-button"
              disabled={!playable}
              title={playable ? "下载视频" : "视频完成后可下载"}
              aria-label="下载视频"
              onClick={onDownload}
            >
              <DownloadIcon />
            </button>
          </div>
        ) : null}
      </div>
      <details ref={menuRef} className="adh-work-actions" onToggle={closeOtherMenus}>
        <summary aria-label={`${work.title}的操作`} title="更多操作">
          <MoreIcon />
        </summary>
        <div className="adh-work-action-list">
          <button
            type="button"
            className="adh-delete-work-button"
            onClick={() => {
              if (menuRef.current) menuRef.current.open = false;
              onDelete();
            }}
          >
            <TrashIcon />
            删除作品
          </button>
        </div>
      </details>
      <div className="adh-work-body">
        <div className="adh-work-title-row">
          <h3 title={work.title}>{work.title}</h3>
          <button
            type="button"
            className="adh-work-rename"
            aria-label={`重命名${work.title}`}
            title="重命名"
            onClick={onRename}
          >
            <PencilIcon />
          </button>
        </div>
        <div className="adh-work-meta">
          <span>{work.date}</span>
        </div>
      </div>
    </article>
  );
};

const WRITING_LENGTHS = [
  { value: "30", label: "约 30 秒" },
  { value: "60", label: "约 1 分钟" },
  { value: "180", label: "约 3 分钟" },
];

const clampScriptDuration = (raw: string) => {
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n)) return 60;
  return Math.min(1800, Math.max(10, n));
};

const workNameFromScript = (text: string) => {
  const line = text.trim().split(/\n/)[0]?.replace(/\s+/g, " ") || "口播视频";
  return line.slice(0, 255);
};

const voiceSourceTypeOf = (source: "public" | "mine") =>
  source === "mine" ? "USER_VOICE_TONE" : "PUBLIC_VOICE_TONE";

export const AiDigitalHumanStudio: FC = () => {
  const navigate = useNavigate();
  const { alertConfig, hideAlert, showWarning, showSuccess } = useAlert();
  const [inputMode, setInputMode] = useState<"text" | "audio">("text");
  const [scriptText, setScriptText] = useState(
    "大家好，欢迎来到本期产品介绍。今天我将用三分钟，带大家了解我们的全新企业级 AI 创作平台。"
  );
  const [selectedAvatar, setSelectedAvatar] = useState("");
  const [selectedAvatarId, setSelectedAvatarId] = useState<number | null>(null);
  const [selectedVoice, setSelectedVoice] = useState("");
  const [selectedVoiceId, setSelectedVoiceId] = useState<number | null>(null);
  const [selectedVoiceSource, setSelectedVoiceSource] = useState<"public" | "mine">("public");
  const [selectedBackground, setSelectedBackground] = useState("演播室");
  const [language, setLanguage] = useState(LANGUAGES[0].value);
  const [ratio, setRatio] = useState("16:9");

  const {
    list: workList,
    loading: worksLoading,
    loadingMore: worksLoadingMore,
    hasMore: worksHasMore,
    hasPending: worksHasPending,
    sentinelRef: worksSentinelRef,
    refresh: refreshWorks,
    removeItem: removeWorkItem,
    patchItem: patchWorkItem,
  } = useVideoInferPage({ pageSize: 12 });

  // 口播视频任务结束后再刷新一次积分，避免生成完成时数字不更新
  const prevWorksPendingRef = useRef(false);
  useEffect(() => {
    if (prevWorksPendingRef.current && !worksHasPending) triggerPointsRefresh();
    prevWorksPendingRef.current = worksHasPending;
  }, [worksHasPending]);
  const { list: avatarOptions } = useDigitalHumanPage({
    scope: DIGITAL_HUMAN_SCOPE.ALL,
    pageSize: 20,
  });
  const { publicVoices, mineVoices } = useDigitalHumanVoiceLists();
  const [audioUploaded, setAudioUploaded] = useState(false);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState("");
  const [audioRemoteUrl, setAudioRemoteUrl] = useState<string | null>(null);
  const [audioDisplayName, setAudioDisplayName] = useState("");
  const [draftId, setDraftId] = useState<number | null>(null);
  const [estimatedPoints, setEstimatedPoints] = useState<number | null>(null);
  const [estimatingPoints, setEstimatingPoints] = useState(false);
  // 回填作品时强制重算一次预估，即使文案/音频没变化
  const [estimateTick, setEstimateTick] = useState(0);
  const [creating, setCreating] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [previewWork, setPreviewWork] = useState<{
    id: string;
    title: string;
    status: WorkStatus;
    type: "talking" | "course";
    videoUrl: string | null;
  } | null>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const [toast, setToast] = useState("");
  const [modal, setModal] = useState<string | null>(null);
  const [workAction, setWorkAction] = useState<{ type: "rename" | "delete"; work: StudioWork } | null>(null);
  const [workActionName, setWorkActionName] = useState("");
  const [workActionSubmitting, setWorkActionSubmitting] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const workNameInputRef = useRef<HTMLInputElement>(null);
  const [writingTopic, setWritingTopic] = useState("");
  const [writingLength, setWritingLength] = useState("60");
  const [writingDraft, setWritingDraft] = useState("");
  const [writingHasResult, setWritingHasResult] = useState(false);
  const [writingBusy, setWritingBusy] = useState(false);
  const [writingConsent, setWritingConsent] = useState(false);
  const writingRequestRef = useRef(0);
  const writingTopicRef = useRef<HTMLInputElement>(null);

  // Layout composer state
  const [layoutBackground, setLayoutBackground] = useState(selectedBackground);
  const [layoutRatio, setLayoutRatio] = useState(ratio);
  const [subtitleEnabled, setSubtitleEnabled] = useState(true);
  const [humanScale, setHumanScale] = useState(80);
  const [humanPos, setHumanPos] = useState({ x: 50, y: 55 });
  const [subtitlePos, setSubtitlePos] = useState({ x: 50, y: 88 });

  const stageRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    target: "human" | "subtitle";
    startX: number;
    startY: number;
    initX: number;
    initY: number;
  } | null>(null);

  const RATIO_MAP: Record<string, string> = {
    "16:9": "16 / 9",
    "9:16": "9 / 16",
    "4:3": "4 / 3",
    "21:9": "21 / 9",
  };

  const selectedAvatarColor = useMemo(
    () => SAMPLE_AVATARS.find((a) => a.name === selectedAvatar)?.color || "#b85c5c",
    [selectedAvatar]
  );

  useEffect(() => {
    if (selectedAvatarId != null) return;
    const first = avatarOptions.find(digitalHumanUsable) ?? avatarOptions[0];
    if (!first) return;
    setSelectedAvatar(first.name);
    setSelectedAvatarId(first.id);
  }, [avatarOptions, selectedAvatarId]);

  useEffect(() => {
    if (selectedVoiceId != null) return;
    const first = publicVoices[0];
    if (!first) return;
    setSelectedVoice(first.name);
    setSelectedVoiceId(first.id);
    setSelectedVoiceSource(first.source);
  }, [publicVoices, selectedVoiceId]);

  // 音色列表异步加载完成或回填草稿时，用 id 反查补上音色名称，避免显示与提交不一致
  useEffect(() => {
    if (selectedVoiceId == null) return;
    const found = (selectedVoiceSource === "mine" ? mineVoices : publicVoices).find(
      (v) => v.id === selectedVoiceId
    );
    if (found?.name && found.name !== selectedVoice) setSelectedVoice(found.name);
  }, [selectedVoiceId, selectedVoiceSource, publicVoices, mineVoices, selectedVoice]);

  const startDrag = (target: "human" | "subtitle", e: React.PointerEvent) => {
    e.preventDefault();
    const pos = target === "human" ? humanPos : subtitlePos;
    dragRef.current = {
      target,
      startX: e.clientX,
      startY: e.clientY,
      initX: pos.x,
      initY: pos.y,
    };
    const move = (ev: PointerEvent) => {
      if (!dragRef.current || !stageRef.current) return;
      const rect = stageRef.current.getBoundingClientRect();
      const dx = ((ev.clientX - dragRef.current.startX) / rect.width) * 100;
      const dy = ((ev.clientY - dragRef.current.startY) / rect.height) * 100;
      const x = Math.max(5, Math.min(95, dragRef.current.initX + dx));
      const y = Math.max(5, Math.min(95, dragRef.current.initY + dy));
      if (dragRef.current.target === "human") setHumanPos({ x, y });
      else setSubtitlePos({ x, y });
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const align = (position: "left" | "center" | "right") => {
    const x = position === "left" ? 20 : position === "right" ? 80 : 50;
    setHumanPos((p) => ({ ...p, x }));
    setSubtitlePos((p) => ({ ...p, x }));
  };

  const resetLayout = () => {
    setHumanPos({ x: 50, y: 55 });
    setSubtitlePos({ x: 50, y: 88 });
    setHumanScale(80);
  };

  const confirmLayout = () => {
    setSelectedBackground(layoutBackground);
    setRatio(layoutRatio);
    closeModal();
    showToast("已确认画面");
  };

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2600);
  };

  const clearAudio = () => {
    audioPlayerRef.current?.pause();
    audioPlayerRef.current = null;
    if (audioPreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(audioPreviewUrl);
    setAudioPreviewUrl("");
    setAudioFile(null);
    setAudioRemoteUrl(null);
    setAudioDisplayName("");
    setAudioDuration(0);
    setAudioUploaded(false);
    if (audioInputRef.current) audioInputRef.current.value = "";
  };

  const hasAudioSource = Boolean(audioFile || audioRemoteUrl);

  const buildAudioReq = (file?: File | null): VideoInfer.SaveReq => ({
    ...(draftId ? { id: draftId } : {}),
    name: file ? workNameFromFile(file) : audioDisplayName.slice(0, 255) || "音频口播",
    direction: ratioToDirection(ratio),
    resolution: "1080p",
    ...captionsPayload(language, subtitleEnabled),
    targetLanguage: language,
    scenes: [
      {
        sceneType: VIDEO_INFER_SCENE_TYPE.AUDIO,
        ...(selectedAvatarId ? { digitalHumanId: selectedAvatarId } : {}),
        ...(!file && audioRemoteUrl ? { audioUrl: audioRemoteUrl } : {}),
      },
    ],
  });

  const pickAudioFile = async (file: File | undefined) => {
    if (!file) return;
    if (!isAllowedAudioFile(file)) {
      showWarning("请上传 MP3、WAV 或 M4A 音频");
      return;
    }
    if (file.size > AUDIO_MAX_BYTES) {
      showWarning(`音频大小需在 100MB 以内，当前：${formatFileSize(file.size)}`);
      return;
    }
    const duration = await readAudioDurationSeconds(file);
    if (audioPreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(audioPreviewUrl);
    setAudioFile(file);
    setAudioRemoteUrl(null);
    setAudioDisplayName(file.name);
    setAudioDuration(duration);
    setAudioPreviewUrl(URL.createObjectURL(file));
    setAudioUploaded(true);
  };

  const rememberDraftId = (data: unknown) => {
    const id = Number(data);
    if (Number.isFinite(id) && id > 0) setDraftId(id);
  };

  const saveDraft = async () => {
    if (savingDraft || creating) return;
    if (inputMode === "audio") {
      if (!hasAudioSource) {
        showWarning("请先上传音频");
        return;
      }
      setSavingDraft(true);
      try {
        const res = audioFile
          ? await saveVideoInferAudioDraftApi(buildAudioReq(audioFile), audioFile)
          : await saveVideoInferDraftApi(buildAudioReq());
        rememberDraftId(res.data);
        showToast("草稿已保存");
        refreshWorks();
      } catch {
        /* request 已弹出错误 */
      } finally {
        setSavingDraft(false);
      }
      return;
    }
    if (!scriptText.trim()) {
      showWarning("请先填写口播文案");
      return;
    }
    setSavingDraft(true);
    try {
      const res = await saveVideoInferDraftApi(buildTextReq());
      rememberDraftId(res.data);
      showToast("草稿已保存");
      refreshWorks();
    } catch {
      /* request 已弹出错误 */
    } finally {
      setSavingDraft(false);
    }
  };

  const previewAudio = () => {
    if (!audioPreviewUrl) return;
    if (!audioPlayerRef.current) {
      audioPlayerRef.current = new Audio(audioPreviewUrl);
    }
    const player = audioPlayerRef.current;
    if (player.paused) void player.play();
    else player.pause();
  };

  const buildTextReq = (): VideoInfer.SaveReq => ({
    name: workNameFromScript(scriptText),
    direction: ratioToDirection(ratio),
    resolution: "1080p",
    ...captionsPayload(language, subtitleEnabled),
    targetLanguage: language,
    scenes: [
      {
        sceneType: VIDEO_INFER_SCENE_TYPE.TEXT,
        textContent: scriptText.trim(),
        ...(selectedAvatarId ? { digitalHumanId: selectedAvatarId } : {}),
        ...(selectedVoiceId
          ? {
              voiceId: selectedVoiceId,
              voiceSourceType: voiceSourceTypeOf(selectedVoiceSource),
            }
          : {}),
      },
    ],
  });

  const submitTextTalking = async () => {
    if (creating) return;
    if (!scriptText.trim()) {
      showWarning("请先填写口播文案");
      return;
    }
    if (!selectedAvatarId) {
      showWarning("请选择数字人");
      return;
    }
    if (!selectedVoiceId) {
      showWarning("请选择声音");
      return;
    }
    setCreating(true);
    try {
      await createVideoInferApi(buildTextReq());
      showToast("已提交生成任务");
      triggerPointsRefresh();
      refreshWorks();
    } catch {
      /* request 已弹出错误 */
    } finally {
      setCreating(false);
    }
  };

  const submitAudioTalking = async () => {
    if (creating) return;
    if (!hasAudioSource) {
      showWarning("请先上传音频");
      return;
    }
    if (!selectedAvatarId) {
      showWarning("请选择数字人");
      return;
    }
    setCreating(true);
    try {
      if (audioFile) {
        await createVideoInferWithAudioApi(
          {
            ...buildAudioReq(audioFile),
            scenes: [
              {
                sceneType: VIDEO_INFER_SCENE_TYPE.AUDIO,
                digitalHumanId: selectedAvatarId,
              },
            ],
          },
          audioFile
        );
      } else {
        await createVideoInferApi({
          ...buildAudioReq(),
          scenes: [
            {
              sceneType: VIDEO_INFER_SCENE_TYPE.AUDIO,
              digitalHumanId: selectedAvatarId,
              ...(audioRemoteUrl ? { audioUrl: audioRemoteUrl } : {}),
            },
          ],
        });
      }
      showToast("已提交生成任务");
      triggerPointsRefresh();
      refreshWorks();
    } catch {
      /* request 已弹出错误 */
    } finally {
      setCreating(false);
    }
  };

  const works = useMemo(
    () =>
      workList.map((item) => ({
        id: String(item.id),
        type: videoInferWorkType(item),
        title: item.name?.trim() || "未命名作品",
        status: videoInferUiStatus(item.status),
        date: formatWorkDate(item.updateTime || item.createTime),
        ratio: workRatio(item),
        videoUrl: resolveVideoInferVideoUrl(item),
        coverUrl: resolveVideoInferCoverUrl(item),
        captions: Number(item.captions) || 0,
        targetLanguage: item.targetLanguage,
        raw: item,
      })),
    [workList]
  );
  useEffect(() => {
    const textReady = inputMode === "text" && Boolean(scriptText.trim());
    const audioReady = inputMode === "audio" && Boolean(audioFile || audioRemoteUrl);
    if (!textReady && !audioReady) {
      setEstimatedPoints(null);
      setEstimatingPoints(false);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      const run = async () => {
        setEstimatingPoints(true);
        try {
          const scenes =
            inputMode === "audio"
              ? [
                  {
                    sceneType: VIDEO_INFER_SCENE_TYPE.AUDIO,
                    durationSeconds: audioDuration > 0 ? audioDuration : 0,
                  },
                ]
              : [
                  {
                    sceneType: VIDEO_INFER_SCENE_TYPE.TEXT,
                    textContent: scriptText.trim(),
                  },
                ];
          const res = await estimateVideoInferPointsApi(scenes);
          if (cancelled) return;
          const points = Number(res.data);
          setEstimatedPoints(Number.isFinite(points) ? points : 0);
        } catch {
          if (!cancelled) setEstimatedPoints(null);
        } finally {
          if (!cancelled) setEstimatingPoints(false);
        }
      };
      void run();
    }, inputMode === "text" ? 400 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [inputMode, audioFile, audioRemoteUrl, audioDuration, scriptText, estimateTick]);

  useEffect(() => () => {
    if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
  }, [audioPreviewUrl]);

  const talkingCount = works.filter((w) => w.type === "talking").length;
  const worksFiltered = works.filter((w) => w.type === "talking");

  const openModal = (id: string) => setModal(id);
  const closeModal = () => setModal(null);
  const closePreview = () => {
    previewVideoRef.current?.pause();
    setPreviewWork(null);
  };

  const openWorkPreview = (w: StudioWork) => {
    if (w.status !== "done" || !w.videoUrl) return;
    setPreviewWork({
      id: w.id,
      title: w.title,
      status: w.status,
      type: w.type,
      videoUrl: w.videoUrl,
    });
  };

  const applyWorkSettings = (w: StudioWork, item?: VideoInfer.Item) => {
    const source = item || w.raw;
    const ratioValue = source ? workRatio(source) : w.ratio;
    setRatio(ratioValue === "9:16" ? "9:16" : "16:9");
    const lang = source?.targetLanguage || w.targetLanguage;
    if (lang) setLanguage(lang);
    // 详情里 captions（中文字幕）与 enCaptions（英文字幕）任一开启，都应还原成「开启」
    if (source && (source.captions != null || source.enCaptions != null)) {
      setSubtitleEnabled(Number(source.captions) === 1 || Number(source.enCaptions) === 1);
    } else if (w.captions != null) {
      setSubtitleEnabled(Number(w.captions) === 1);
    }
    document.querySelector(".adh-composer")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const applyWorkContent = async (w: StudioWork) => {
    let item = w.raw;
    const workId = Number(w.id);
    let detailLoaded = false;
    try {
      if (Number.isFinite(workId) && workId > 0) {
        const res = await getVideoInferApi(workId);
        if (res?.data) {
          item = res.data;
          detailLoaded = true;
        }
      }
    } catch {
      /* request 已弹出错误 */
    }
    if (!detailLoaded && Number.isFinite(workId) && workId > 0) {
      showWarning("未取到作品详情，已按列表信息回填，请核对文案与声音");
    }
    applyWorkSettings(w, item);
    const scene = pickVideoInferScene(item);
    const audioUrl = resolveVideoInferMediaUrl(scene?.audioUrl);
    // 只看场景类型判断输入方式，不能因为存在 audioUrl 就当成音频作品
    const isAudio = Number(scene?.sceneType) === VIDEO_INFER_SCENE_TYPE.AUDIO;
    if (isAudio) {
      setInputMode("audio");
      if (audioPreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(audioPreviewUrl);
      setAudioFile(null);
      setAudioRemoteUrl(audioUrl);
      setAudioPreviewUrl(audioUrl || "");
      setAudioUploaded(Boolean(audioUrl));
      setAudioDisplayName(item?.name?.trim() || w.title || "已上传音频");
      const knownDuration = Number(scene?.durationSeconds);
      setAudioDuration(
        Number.isFinite(knownDuration) && knownDuration > 0
          ? knownDuration
          : audioUrl
            ? await readAudioDurationFromUrl(audioUrl)
            : 0
      );
      audioPlayerRef.current?.pause();
      audioPlayerRef.current = null;
    } else {
      setInputMode("text");
      clearAudio();
      const text = (scene?.textContent || item?.textContent || item?.name || w.title || "").trim();
      setScriptText(text);
    }
    if (scene?.digitalHumanId) {
      setSelectedAvatarId(scene.digitalHumanId);
      const found = avatarOptions.find((a) => a.id === scene.digitalHumanId);
      if (found?.name) setSelectedAvatar(found.name);
    }
    if (scene?.voiceId) {
      setSelectedVoiceId(scene.voiceId);
      const mine = scene.voiceSourceType === "USER_VOICE_TONE";
      setSelectedVoiceSource(mine ? "mine" : "public");
      const found = (mine ? mineVoices : publicVoices).find((v) => v.id === scene.voiceId);
      if (found?.name) setSelectedVoice(found.name);
    }
    // 回填后强制重算一次预估积分
    setEstimateTick((tick) => tick + 1);
  };

  const resumeDraft = async (w: StudioWork) => {
    if (w.status !== "draft") return;
    const id = Number(w.id);
    if (Number.isFinite(id) && id > 0) setDraftId(id);
    await applyWorkContent(w);
    showSuccess("已载入草稿，可继续编辑");
  };

  const copyWork = async (w: StudioWork) => {
    setDraftId(null);
    await applyWorkContent(w);
    showSuccess("已载入作品内容，将作为新草稿编辑，原作品保持不变");
  };

  const downloadWork = (w: StudioWork) => {
    if (!w.videoUrl) {
      showToast("视频完成后可下载");
      return;
    }
    const link = document.createElement("a");
    link.href = w.videoUrl;
    link.download = `${w.title || "作品"}.mp4`;
    link.target = "_blank";
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const openRenameWork = (w: StudioWork) => {
    setWorkAction({ type: "rename", work: w });
    setWorkActionName(w.title);
    window.setTimeout(() => workNameInputRef.current?.focus(), 50);
  };

  const openDeleteWork = (w: StudioWork) => {
    if (w.status === "processing" || w.status === "queued") {
      showWarning("创作中或排队中的作品暂不可删除");
      return;
    }
    setWorkAction({ type: "delete", work: w });
  };

  const closeWorkAction = () => {
    if (workActionSubmitting) return;
    setWorkAction(null);
    setWorkActionName("");
  };

  const confirmWorkAction = async () => {
    if (!workAction || workActionSubmitting) return;
    const id = Number(workAction.work.id);
    if (!Number.isFinite(id) || id <= 0) return;
    if (workAction.type === "rename" && !workActionName.trim()) {
      showWarning("请输入作品名称");
      return;
    }
    setWorkActionSubmitting(true);
    try {
      if (workAction.type === "rename") {
        const name = workActionName.trim();
        await renameVideoInferApi({ id, name });
        setWorkAction(null);
        setWorkActionName("");
        if (previewWork?.id === workAction.work.id) {
          setPreviewWork({ ...previewWork, title: name });
        }
        patchWorkItem(id, { name });
        showSuccess("作品已重命名");
      } else {
        await deleteVideoInferApi(id);
        setWorkAction(null);
        setWorkActionName("");
        if (draftId === id) setDraftId(null);
        if (previewWork?.id === workAction.work.id) setPreviewWork(null);
        removeWorkItem(id);
        showSuccess("作品已删除");
      }
    } catch {
      /* request 已弹出错误 */
    } finally {
      setWorkActionSubmitting(false);
    }
  };

  const retryWork = async (w: StudioWork) => {
    if (w.status !== "failed" || retryingId) return;
    const id = Number(w.id);
    if (!Number.isFinite(id) || id <= 0) return;
    setRetryingId(w.id);
    try {
      await retryVideoInferApi(id);
      refreshWorks();
      showSuccess("已提交重试");
    } catch {
      /* request 已弹出错误 */
    } finally {
      setRetryingId(null);
    }
  };

  const openWriting = () => {
    writingRequestRef.current += 1;
    setWritingHasResult(false);
    setWritingConsent(false);
    setWritingBusy(false);
    openModal("writing");
  };

  const canApplyWriting = Boolean(writingDraft.trim() && (!scriptText.trim() || writingConsent));

  const generateWriting = async () => {
    const topic = writingTopic.trim();
    if (!topic) {
      showToast("请先填写口播主题");
      writingTopicRef.current?.focus();
      return;
    }
    const request = ++writingRequestRef.current;
    setWritingBusy(true);
    setWritingHasResult(false);
    setWritingConsent(false);
    try {
      const res = await genVideoInferScriptApi({
        topic: topic.slice(0, 200),
        durationSeconds: clampScriptDuration(writingLength),
      });
      if (request !== writingRequestRef.current) return;
      const script = res.data?.script?.trim() || "";
      if (!script) {
        showWarning("未生成口播稿，请重试");
        return;
      }
      setWritingDraft(script.slice(0, 5000));
      setWritingHasResult(true);
    } catch {
      /* request 已弹出错误 */
    } finally {
      if (request === writingRequestRef.current) setWritingBusy(false);
    }
  };

  const applyWriting = () => {
    if (!canApplyWriting) return;
    setScriptText(writingDraft);
    closeModal();
    showToast("文稿已填入，可继续编辑；尚未生成音频或视频");
  };

  return (
    <div className="adh-page adh-page--studio">
      <AlertMessage config={alertConfig} onClose={hideAlert} />
      <header className="adh-hero">
        <span className="adh-kicker"><i />Digital Human Studio</span>
        <h1>AI 数字人</h1>
        <p>通过文案、音频或课件，创建口播视频与视频课。</p>
      </header>

      <section className="adh-card adh-composer">
        <div className="adh-input-tabs" role="tablist">
          <button className={inputMode === "text" ? "active" : ""} type="button" onClick={() => setInputMode("text")}>
            <span><InputIcon name="text" /></span>文案输入<small>口播视频</small>
          </button>
          <button className={inputMode === "audio" ? "active" : ""} type="button" onClick={() => setInputMode("audio")}>
            <span><InputIcon name="audio" /></span>音频上传<small>口播视频</small>
          </button>
          {/* <button className="disabled" type="button" disabled title="PPT 文档输入暂不可用">
            <span><InputIcon name="ppt" /></span>PPT 文档<small>视频课</small>
          </button> */}
        </div>

        {inputMode === "text" && (
          <div className="adh-input-pane">
            <div className="adh-script-label-row">
              <label className="adh-field-label">口播文案</label>
              <button className="adh-ai-write-btn" type="button" onClick={openWriting}>
                <span className="adh-ai-write-mark" aria-hidden="true">
                  <UiIcon d="m12 3 2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5zM20 2v4m-2-2h4" />
                </span>
                <span>AI 帮我写</span>
              </button>
            </div>
            <div className="adh-script-shell">
              <textarea
                value={scriptText}
                onChange={(e) => setScriptText(e.target.value)}
                maxLength={5000}
                placeholder="请输入口播文案，也可以点击「AI 帮我写」生成文稿。"
              />
              <span className="adh-counter">{scriptText.length.toLocaleString()} / 5,000</span>
            </div>
          </div>
        )}

        {inputMode === "audio" && (
          <div className="adh-input-pane">
            <input
              ref={audioInputRef}
              type="file"
              accept={AUDIO_ACCEPT}
              hidden
              onChange={(e) => {
                void pickAudioFile(e.target.files?.[0]);
                e.currentTarget.value = "";
              }}
            />
            {!audioUploaded || !hasAudioSource ? (
              <button
                className="adh-upload-zone"
                type="button"
                onClick={() => audioInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  void pickAudioFile(e.dataTransfer.files?.[0]);
                }}
              >
                <span className="adh-upload-icon"><UploadIcon /></span>
                <strong>点击上传或拖拽音频文件到此处</strong>
                <em>MP3、WAV、M4A · ≤ 100MB</em>
              </button>
            ) : (
              <div className="adh-file-card">
                <span className="adh-file-type"><AudioFileIcon /></span>
                <span>
                  <strong>{audioFile?.name || audioDisplayName || "已上传音频"}</strong>
                  <small>
                    {audioFile
                      ? `${formatFileSize(audioFile.size)} · ${formatDuration(audioDuration)} · ${fileExt(audioFile.name)}`
                      : formatDuration(audioDuration)}
                  </small>
                </span>
                <button type="button" aria-label="试听上传音频" title="试听音频" onClick={previewAudio}>
                  <PlayIcon />
                </button>
                <button type="button" aria-label="删除上传音频" title="删除" onClick={clearAudio}>
                  <TrashIcon />
                </button>
              </div>
            )}
          </div>
        )}

        <div className={`adh-settings${inputMode === "audio" ? " is-audio" : ""}`}>
          <div className="adh-settings-head">
            <strong>视频设置</strong>
          </div>
          <div className="adh-asset-row">
            <div className="adh-compact-field">
              <small>数字人</small>
              <button type="button" className="adh-compact-select" onClick={() => openModal("picker-avatar")}>
                <strong>{selectedAvatar || "请选择"}</strong>
                <ChevronIcon />
              </button>
            </div>
            {inputMode !== "audio" ? (
              <div className="adh-compact-field">
                <small>声音</small>
                <button type="button" className="adh-compact-select" onClick={() => openModal("picker-voice")}>
                  <strong>{selectedVoice || "请选择"}</strong>
                  <ChevronIcon />
                </button>
              </div>
            ) : null}
          </div>
          <div className="adh-output-row">
            <label className="adh-compact-field">
              <small>合成语言</small>
              <NativeSelect value={language} options={LANGUAGES} onChange={setLanguage} />
            </label>
            <label className="adh-compact-field">
              <small>视频方向</small>
              <NativeSelect
                value={ratio === "9:16" ? "9:16" : "16:9"}
                options={[
                  { value: "16:9", label: "横版" },
                  { value: "9:16", label: "竖版" },
                ]}
                onChange={setRatio}
              />
            </label>
            <label className="adh-compact-field">
              <small>字幕</small>
              <NativeSelect
                value={captionsLanguageSupported(language) && subtitleEnabled ? "1" : "0"}
                options={[
                  { value: "1", label: "开启" },
                  { value: "0", label: "关闭" },
                ]}
                disabled={!captionsLanguageSupported(language)}
                onChange={(value) => setSubtitleEnabled(value === "1")}
              />
            </label>
          </div>

        </div>

        <div className="adh-composer-footer">
          {/* 预估积分先不展示 */}
          <span />
          <div className="adh-composer-footer-actions">
          <button
            className="adh-secondary-btn"
            type="button"
            disabled={
              savingDraft ||
              creating ||
              (inputMode === "audio" ? !hasAudioSource : !scriptText.trim())
            }
            onClick={() => void saveDraft()}
          >
            {savingDraft ? "保存中…" : "保存草稿"}
          </button>
          <button
            className="adh-primary-btn"
            type="button"
            disabled={
              creating ||
              savingDraft ||
              (inputMode === "audio" ? !hasAudioSource : !scriptText.trim())
            }
            onClick={() => {
              if (inputMode === "audio") void submitAudioTalking();
              else void submitTextTalking();
            }}
          >
            {creating ? "提交中…" : "生成口播视频"} <span>→</span>
          </button>
          </div>
        </div>
      </section>

      <section className="adh-section adh-works">
        <div className="adh-section-head">
          <h2>我的作品</h2>
          <div className="adh-section-links">
            <button className="adh-secondary-btn" type="button" onClick={() => navigate("/hub/ai-digital-human/avatars")}>我的数字人</button>
          </div>
        </div>
        <div className="adh-filter-tabs">
          <button className="active" type="button">
            口播视频 <b>{talkingCount}</b>
          </button>
        </div>
        <Spin spinning={worksLoading && works.length === 0}>
          {worksFiltered.length === 0 && !worksLoading ? (
            <p className="adh-picker-empty">没有符合条件的作品</p>
          ) : (
            <div className="adh-work-grid">
              {worksFiltered.map((w) => (
                <WorkCard
                  key={w.id}
                  work={w}
                  onPreview={() => openWorkPreview(w)}
                  onCopy={() => void copyWork(w)}
                  onDownload={() => downloadWork(w)}
                  onRename={() => openRenameWork(w)}
                  onDelete={() => openDeleteWork(w)}
                  onResume={() => void resumeDraft(w)}
                  onRetry={() => void retryWork(w)}
                  retrying={retryingId === w.id}
                />
              ))}
            </div>
          )}
        </Spin>
        <div className="adh-infinite-status" ref={worksSentinelRef}>
          {worksLoadingMore
            ? "加载中…"
            : worksHasMore
              ? "下滑加载更多"
              : works.length > 0
                ? "没有更多了"
                : ""}
        </div>
      </section>

      <AutoModel
        open={modal === "writing"}
        onClose={closeModal}
        title={
          <div className="adh-writing-title">
            <span>AI 帮我写</span>
            <p>先生成可编辑的新稿，确认使用后才会填入口播文案。</p>
          </div>
        }
        size="md"
        contentMaxWidth={650}
        contentClassName="adh-writing-shell"
        bodyClassName="adh-writing-body"
        footerRight={
          <>
            <Button onClick={closeModal}>取消</Button>
            <Button type="primary" disabled={!canApplyWriting} onClick={applyWriting}>
              使用这篇文稿
            </Button>
          </>
        }
      >
        <label className="adh-writing-field">
          <span>口播主题</span>
          <input
            ref={writingTopicRef}
            maxLength={200}
            value={writingTopic}
            onChange={(e) => setWritingTopic(e.target.value)}
            placeholder="例如：向新员工介绍公司的报销流程"
          />
        </label>
        <div className="adh-writing-options">
          <div className="adh-writing-length">
            <span>目标时长</span>
            <NativeSelect
              className="adh-select--md"
              value={writingLength}
              options={WRITING_LENGTHS}
              onChange={setWritingLength}
            />
          </div>
          <button
            className={`adh-secondary-btn adh-generate-writing-btn${writingTopic.trim() ? " is-ready" : ""}`}
            type="button"
            disabled={writingBusy}
            onClick={generateWriting}
          >
            {writingBusy ? "生成中…" : writingHasResult ? "重新生成新稿" : "生成新稿"}
          </button>
        </div>
        {writingHasResult && (
          <div className="adh-writing-result">
            <label htmlFor="adhWritingDraft">新稿预览 · 可继续编辑</label>
            <textarea
              id="adhWritingDraft"
              maxLength={5000}
              value={writingDraft}
              onChange={(e) => setWritingDraft(e.target.value)}
            />
            <p>确认使用后才会填入口播文案。</p>
          </div>
        )}
        {writingHasResult && Boolean(scriptText.trim()) && (
          <label className="adh-replace-consent">
            <input
              type="checkbox"
              checked={writingConsent}
              onChange={(e) => setWritingConsent(e.target.checked)}
            />
            我确认用新稿替换当前已有文案
          </label>
        )}
      </AutoModel>

      <AutoModel
        open={Boolean(previewWork?.videoUrl && previewWork.status === "done")}
        onClose={closePreview}
        title={
          <div className="adh-writing-title">
            <span>{previewWork?.title || "查看作品"}</span>
            <p>
              {previewWork
                ? `${statusText(previewWork.status)} · ${previewWork.type === "course" ? "视频课" : "口播视频"}`
                : ""}
            </p>
          </div>
        }
        size="md"
        contentMaxWidth={720}
        contentClassName="adh-work-preview-shell"
        bodyClassName="adh-work-preview-body"
        footerRight={
          <Button onClick={closePreview}>关闭</Button>
        }
      >
        <div className="adh-work-preview-content">
          {previewWork?.videoUrl && previewWork.status === "done" ? (
            <div className="adh-work-preview-player">
              <video
                ref={previewVideoRef}
                src={previewWork.videoUrl}
                controls
                playsInline
                preload="metadata"
              />
            </div>
          ) : (
            <p>
              {previewWork?.status === "done"
                ? "当前作品暂无成片，不能播放。"
                : previewWork?.status === "failed"
                  ? "作品生成失败，暂不能播放。"
                  : "作品尚未生成完成，暂不能播放。"}
            </p>
          )}
        </div>
      </AutoModel>

      {modal === "picker-avatar" && (
        <AvatarPickerModal
          value={selectedAvatar}
          valueId={selectedAvatarId}
          onClose={closeModal}
          onConfirm={(avatar) => {
            setSelectedAvatar(avatar.name);
            setSelectedAvatarId(avatar.id);
            closeModal();
            showToast("已选择 " + avatar.name);
          }}
        />
      )}

      {modal === "picker-voice" && (
        <VoicePickerModal
          valueId={selectedVoiceId}
          onClose={closeModal}
          onConfirm={(voice) => {
            setSelectedVoice(voice.name);
            setSelectedVoiceId(voice.id);
            setSelectedVoiceSource(voice.source);
            closeModal();
            showToast("已选择 " + voice.name);
          }}
        />
      )}

      {modal === "background" && (
        <BackgroundPickerModal
          value={selectedBackground}
          onClose={closeModal}
          onConfirm={(bg) => { setSelectedBackground(bg); closeModal(); showToast("已使用背景 " + bg); }}
        />
      )}

      {modal === "layout-background" && (
        <BackgroundPickerModal
          value={layoutBackground}
          onClose={() => setModal("layout")}
          onConfirm={(bg) => { setLayoutBackground(bg); setModal("layout"); }}
        />
      )}

      <AutoModel
        open={modal === "layout"}
        onClose={closeModal}
        title={
          <div className="adh-writing-title">
            <span>调整口播画面</span>
            <p>拖动调整位置，拖动右下角或使用滑杆等比缩放。</p>
          </div>
        }
        size="xl"
        contentMaxWidth={980}
        contentClassName="adh-layout-shell"
        bodyClassName="adh-layout-body"
        footerLeft={
          <div className="adh-layout-hint">
            仅调整当前口播构图，不改变文稿和音频。
          </div>
        }
        footerRight={
          <>
            <Button onClick={closeModal}>取消</Button>
            <Button type="primary" onClick={confirmLayout}>确认画面</Button>
          </>
        }
      >
        <div className="adh-layout-toolbar adh-layout-toolbar--top">
          <button
            type="button"
            className="adh-layout-bg-trigger"
            onClick={() => setModal("layout-background")}
          >
            <span className="adh-layout-bg-thumb" style={{ background: BG_COLORS[layoutBackground] }} />
            <span className="adh-layout-bg-text">
              <small>背景</small>
              <strong>{layoutBackground}</strong>
            </span>
            <svg className="adh-caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>

          <div className="adh-layout-field adh-layout-field--ratio">
            <label>视频比例</label>
            <div className="adh-select-wrap">
              <select value={layoutRatio} onChange={(e) => setLayoutRatio(e.target.value)}>
                {RATIOS.map((r) => (<option key={r}>{r}</option>))}
              </select>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </div>
          </div>
        </div>

        <div className="adh-layout-subtitle-row">
          <label>字幕</label>
          <button
            type="button"
            className={`adh-switch ${subtitleEnabled ? "on" : ""}`}
            onClick={() => setSubtitleEnabled((v) => !v)}
            aria-checked={subtitleEnabled}
            role="switch"
          >
            <i />
          </button>
        </div>

        <div className="adh-layout-stage-area">
          <div
            className={`adh-layout-stage${isTransparentBg(layoutBackground) ? " is-transparent" : ""}`}
            ref={stageRef}
            style={{ aspectRatio: RATIO_MAP[layoutRatio], background: isTransparentBg(layoutBackground) ? undefined : BG_COLORS[layoutBackground] }}
          >
            <div
              className="adh-layout-human"
              style={{
                left: `${humanPos.x}%`,
                top: `${humanPos.y}%`,
                transform: `translate(-50%, -50%) scale(${humanScale / 100})`,
                background: selectedAvatarColor,
              }}
              onPointerDown={(e) => startDrag("human", e)}
            >
              <span className="adh-layout-human-label">思萌 · 透明形象示意</span>
              <span />
            </div>
            {subtitleEnabled && (
              <div
                className="adh-layout-subtitle"
                style={{ left: `${subtitlePos.x}%`, top: `${subtitlePos.y}%` }}
                onPointerDown={(e) => startDrag("subtitle", e)}
              >
                大家好，欢迎来到本期产品介绍。今天我将用三分钟，带大家了解我们的全新企业级…
              </div>
            )}
          </div>
        </div>

        <div className="adh-layout-footer__row adh-layout-footer__row--actions">
          <div className="adh-layout-actions-left">
            <button type="button" onClick={() => align("left")}>居左</button>
            <button type="button" onClick={() => align("center")}>居中</button>
            <button type="button" onClick={() => align("right")}>居右</button>
            <button type="button" onClick={resetLayout}>重置</button>
          </div>
          <div className="adh-layout-scale">
            <label>大小</label>
            <input
              type="range"
              min={20}
              max={120}
              value={humanScale}
              onChange={(e) => setHumanScale(Number(e.target.value))}
            />
            <span>{humanScale}%</span>
          </div>
        </div>
      </AutoModel>

      <AutoModel
        open={Boolean(workAction)}
        onClose={closeWorkAction}
        title={workAction?.type === "rename" ? "重命名作品" : "删除作品"}
        size="sm"
        footerRight={
          <>
            <Button disabled={workActionSubmitting} onClick={closeWorkAction}>
              取消
            </Button>
            <Button
              type="primary"
              danger={workAction?.type === "delete"}
              loading={workActionSubmitting}
              onClick={() => void confirmWorkAction()}
            >
              {workAction?.type === "rename" ? "保存名称" : "确认删除"}
            </Button>
          </>
        }
      >
        {workAction?.type === "rename" ? (
          <label className="adh-writing-field">
            <span>作品名称</span>
            <input
              ref={workNameInputRef}
              maxLength={255}
              value={workActionName}
              onChange={(e) => setWorkActionName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void confirmWorkAction();
                }
              }}
              placeholder="请输入作品名称"
            />
          </label>
        ) : (
          <p className="adh-delete-avatar-copy">
            确定删除“{workAction?.work.title}”？草稿、已完成或失败的作品可删除，其他作品不受影响。
          </p>
        )}
      </AutoModel>

      {toast && <div className="adh-toast" role="status">{toast}</div>}
    </div>
  );
};
